import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { FlaggedListItems } from '@/components/QueueFlags/FlaggedListItems';
import { getThumbnailUrl, getOriginalUrl, getStrictThumbnailUrl } from '@/helpers/documentHelpers';
import { trimFileNames } from '@/helpers/trimFileNames';
import { useDocumentUrl } from '@/hooks/useDocumentUrl';
import { downloadFile } from '@/services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import {
  Box,
  Button,
  Icon,
  Popover,
  CheckboxInput,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useContext, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  DocumentDocument,
  DocumentFile,
  documentTitles,
  Flag as FlagType,
  MilestonePackageRoute,
  MilestoneRouteToQueueTypeMap,
  PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES,
  RequirementFlagSettingsType,
  UserPermissions,
  ViewUrlType,
} from 'types';

import { useArchiveDocumentMutation } from '../../../services/documents';
import RequirePermissions from '../../auth/requirePermissions';
import DateTimeDisplay from '../../DateTime';
import { Flag } from '../../QueueFlags/Flag';
import { FlagInfo } from '../../QueueFlags/FlagInfo';
import { QueueFlagsContext } from '../../QueueFlags/QueueFlagsContext';

import styles from './DocumentListItem.module.scss';

interface DocumentListItemProps {
  allowArchive?: boolean;
  item: DocumentDocument;
  index?: number;
  showDocumentActions?: boolean;
  showTypeLabel?: boolean;
  showCheckboxes?: boolean;
  setCheckedItems?: React.Dispatch<React.SetStateAction<string[]>>;
  checkedItems?: string[];
  baseCategory?: string;
  flagType?: RequirementFlagSettingsType;
  radius?: string;
  displayDate?: boolean;
  onClick: (fileIndex: number) => void;
  isPackageEditable?: boolean;
  showFlagToEPC?: boolean;
  showFlagIndicator?: boolean;
}

const ImageRenderer = ({
  isImage,
  hasThumbnail,
  thumbnailUrl,
  fileName,
  hasFlags,
  onClick,
  flagDrivenPackageReview,
  onImageError,
}: {
  isImage: boolean;
  hasThumbnail: boolean;
  thumbnailUrl?: string;
  fileName?: string;
  hasFlags: boolean;
  onClick: (index: number) => void;
  flagDrivenPackageReview: boolean;
  onImageError?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}) => (
  <Box onClick={() => onClick(0)} style={{ cursor: 'pointer' }}>
    {isImage || hasThumbnail ? (
      <Box
        width={{ base: 'md', tablet: 'lg' }}
        margin={{ base: '0 0 0 sm', tablet: 'sm lg sm lg' }}
        borderWidth="xs"
        borderColor="default"
      >
        <img className={styles.image} src={thumbnailUrl} alt={fileName} loading="lazy" onError={onImageError} />
      </Box>
    ) : (
      <Box
        alignItems="flex-start"
        width={{ base: 'md', tablet: 'lg' }}
        padding={{ base: 'sm', tablet: 'sm md md lg' }}
        margin={
          flagDrivenPackageReview
            ? { base: '0 2xl 0 0', tablet: '0 3xl 0 0' }
            : hasFlags
              ? { base: '0 xl 0 0', tablet: '0 2xl 0 0' }
              : { base: '0 2xl 0 0', tablet: '0 4xl 0 0' }
        }
      >
        <Icon name="document" size="3xl" color="body-tertiary" />
      </Box>
    )}
  </Box>
);

const FileInfo = ({
  files,
  itemId,
  documentTitle,
  showTypeLabel,
  getFlag,
  getFlagsByFlagType,
  flagType,
  flagDrivenPackageReview,
  isPhone,
  onClick,
  hasFlags,
  isImage,
  internalReferences,
}: {
  files: DocumentFile[];
  itemId: string;
  documentTitle: string;
  showTypeLabel: boolean;
  getFlag: any;
  getFlagsByFlagType: any;
  flagType?: RequirementFlagSettingsType;
  flagDrivenPackageReview: boolean;
  isPhone: boolean;
  onClick: (index: number) => void;
  hasFlags: boolean;
  isImage?: boolean;
  internalReferences?: { fieldname: string; value: string }[];
}) => {
  return (
    <Box
      width={
        flagDrivenPackageReview
          ? { base: '2xl', tablet: '50' }
          : hasFlags
            ? { base: 'xl', tablet: isImage ? '2xl' : '25' }
            : { base: '2xl', tablet: '50' }
      }
      display="block"
      childGap="xs"
      padding={{ base: '0 lg', tablet: 'md md md 0' }}
      onClick={() => onClick(0)}
      style={{ cursor: 'pointer' }}
    >
      <Box display="block">
        {files.map((file: DocumentFile, index: number) => {
          const dataKeyWithSuffix = `${itemId}-${index}`;
          const currentFlag = getFlag([dataKeyWithSuffix, itemId], file.originalName, false);
          const currentFlags = flagType
            ? getFlagsByFlagType(flagType, false, [dataKeyWithSuffix, itemId], file.originalName)
            : [];

          const allFlags = [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : [])];
          const isAttachedToFlag = allFlags.some(
            (f: FlagType) => !f.resolved && (f.attachedDocumentIds?.includes(itemId) ?? false),
          );
          const hasFlags = Boolean(
            flagDrivenPackageReview ? allFlags?.length || isAttachedToFlag : currentFlag || isAttachedToFlag,
          );

          return (
            <Box
              className={classNames({
                [styles.fileName]: isPhone,
              })}
              key={`${file.md5Hash}-${index}`}
              title={file.originalName}
              display="flex"
              direction="row"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onClick(index);
              }}
            >
              {' '}
              {trimFileNames(file.originalName)}
              {hasFlags && files.length > 1 && (
                <Box margin="2xs 0 0 xs" color="danger" direction="row" alignItems="center" fontSize="xs">
                  <Icon name="flag" size="xs" color="danger" /> {hasFlags ? `(${allFlags.length})` : ''}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {documentTitle && showTypeLabel && <Box fontSize="xs">{documentTitle}</Box>}
      {internalReferences?.length
        ? internalReferences.map((ref, index) =>
            ref?.value ? (
              <Box key={`${ref.fieldname}-${index}`} fontSize="xs" color="body-secondary">
                {ref.value}
              </Box>
            ) : null,
          )
        : null}
    </Box>
  );
};

const DateDisplay = ({
  displayDate,
  meta,
  documentCreatedAtFormat,
}: {
  displayDate: boolean;
  meta: any;
  documentCreatedAtFormat: string;
}) => (
  <Box
    width={{ base: '2xl', tablet: '3xl' }}
    childGap="xs"
    padding={{ base: '0 lg', tablet: 'md md md 0' }}
    margin={{ base: '0 0 xs 0', tablet: '0 0 0 4xl' }}
    direction={{ base: 'column', tablet: 'row' }}
    justifyContent={{ base: 'flex-start', tablet: 'flex-end' }}
  >
    {displayDate ? (
      <DateTimeDisplay
        displayLocalDateTime={true}
        value={meta?.createdAt || ''}
        excludeTime={true}
        dateFormat={documentCreatedAtFormat}
        fontSize="md"
        hoverDateFormat="DD h:mm:ss a"
      />
    ) : null}
  </Box>
);

const ActionButtons = ({
  originalUrl,
  fileName,
  onClick,
}: {
  originalUrl?: string;
  fileName?: string;
  onClick: (index: number) => void;
}) => (
  <Box
    direction={{ base: 'column', tablet: 'row' }}
    childGap="xs"
    borderColor="separator"
    borderWidth={{ tablet: '0 sm 0 sm' }}
  >
    <Button iconPrefix="arrows-maximize" variant="tertiary" size="md" tone="neutral" onClick={() => onClick(0)} />
    <Button
      iconPrefix="launch-app"
      variant="tertiary"
      href={originalUrl}
      as="a"
      size="md"
      target="_blank"
      tone="neutral"
    />
    <Button
      aria-label="Download"
      iconPrefix="download"
      variant="tertiary"
      size="md"
      tone="neutral"
      onClick={async () => {
        if (!originalUrl) return;
        try {
          await downloadFile(originalUrl, fileName ?? 'download');
        } catch {
          toast.error('Failed to download file. Please try again later.');
        }
      }}
    />
  </Box>
);

const DocumentActions = ({
  showDocumentActions,
  allowArchive,
  isOpen,
  isArchiving,
  handleArchive,
  handleClose,
  handleToggle,
  shouldHide,
  item,
  itemId,
  fileName,
  type,
  baseCategory,
  flagType,
  flagDrivenPackageReview,
  hasFlags,
}: {
  showDocumentActions: boolean;
  allowArchive: boolean;
  isOpen: boolean;
  isArchiving: boolean;
  handleArchive: () => void;
  handleClose: () => void;
  handleToggle: () => void;
  shouldHide: boolean;
  item: DocumentDocument;
  itemId: string;
  fileName?: string;
  type?: string;
  baseCategory?: string;
  flagType?: RequirementFlagSettingsType;
  flagDrivenPackageReview: boolean;
  hasFlags: boolean;
}) =>
  showDocumentActions ? (
    <RequirePermissions permissions={[UserPermissions.admin, UserPermissions.editor]} checkAllPermissions={false}>
      <Box
        childGap="xs"
        padding={
          flagDrivenPackageReview ? { base: '0 lg', tablet: 'md xs md 0' } : { base: '0 lg', tablet: 'md lg md 0' }
        }
        direction={{ base: 'column', tablet: 'row' }}
        justifyContent={{ base: 'flex-start', tablet: 'flex-end' }}
        alignItems={{ base: 'center' }}
      >
        {!shouldHide && item.id && (
          <Flag
            baseCategory={baseCategory}
            dataKey={`${itemId}-0`}
            fileName={fileName}
            type={type}
            flagType={flagType}
            padding="xs"
            borderColor="separator"
            borderWidth="0 sm 0 0"
            as="button"
            hasFlags={hasFlags}
          />
        )}
        {allowArchive && (
          <Popover
            content={
              <Box childGap="xs">
                <Button size="sm" variant="secondary" tone="neutral" isLoading={isArchiving} onClick={handleArchive}>
                  Archive
                </Button>
              </Box>
            }
            isOpen={isOpen}
            withPortal
            portalTarget={document.body}
            onClickOutside={() => handleClose()}
            placement="bottom-start"
            offsetFromTarget={-20}
            contentContainerProps={{
              padding: 'sm',
              background: 'grey-800',
            }}
          >
            <Button
              iconPrefix="dots"
              variant="tertiary"
              size="md"
              tone="neutral"
              aria-label="documentActions"
              onClick={handleToggle}
            />
          </Popover>
        )}
      </Box>
    </RequirePermissions>
  ) : null;

const FlagContent = ({
  files,
  itemId,
  flagType,
  allFlagsForItem,
  getFlag,
  flagDrivenPackageReview,
  hasFlags,
  attachedDocumentFlags,
}: {
  files: DocumentFile[];
  itemId: string;
  allFlagsForItem?: any[];
  flagType?: RequirementFlagSettingsType;
  getFlag?: any;
  flagDrivenPackageReview: boolean;
  hasFlags: boolean;
  attachedDocumentFlags?: any[];
}) => {
  if (flagDrivenPackageReview) {
    return (
      <Box childGap="2xs" padding="lg" width="100">
        {files.length > 1 ? (
          <FlaggedListItems files={files} itemId={itemId} flagType={flagType} />
        ) : (
          files.map((file, index) => {
            const possibleDataKeys = new Set([itemId, `${itemId}-${index}`]);
            const dataKeys =
              (allFlagsForItem &&
                allFlagsForItem?.length > 0 &&
                allFlagsForItem
                  .filter(
                    (flag) =>
                      flag.dataKey != null &&
                      flag.fileName === file.originalName &&
                      possibleDataKeys.has(flag.dataKey),
                  )
                  .map((flag: FlagType) => flag.dataKey as string)) ||
              [];
            const directFlags = (attachedDocumentFlags || []).filter(
              (f) => !dataKeys.some((dk: string) => dk === f.dataKey),
            );
            return (
              <FlagInfo
                key={dataKeys[0] || 'attached'}
                dataKey={dataKeys[0]}
                dataKeys={dataKeys}
                fileName={file?.originalName}
                flagType={flagType}
                hasFlags={hasFlags}
                directFlags={directFlags}
              />
            );
          })
        )}
      </Box>
    );
  }

  return (
    <>
      <Box display="block" childGap="xs" padding={{ base: 'md lg', tablet: 'sm md md lg' }}>
        <Icon name="flag" size="lg" color="danger" />
      </Box>
      <Box
        display="block"
        childGap="2xs"
        padding={{ base: '0 lg', tablet: 'md md md 0' }}
        margin="0 2xl 0 0"
        width={{ base: '2xl' }}
      >
        {files.length > 1 ? (
          <FlaggedListItems files={files} itemId={itemId} flagType={flagType} />
        ) : (
          files.map((file, index) => {
            const dataKey = getFlag([itemId], file.originalName, false) ? itemId : `${itemId}-${index}`;
            return (
              <FlagInfo
                key={itemId}
                dataKey={dataKey}
                fileName={file.originalName}
                flagType={flagType}
                hasFlags={hasFlags}
              />
            );
          })
        )}
      </Box>
    </>
  );
};

export function DocumentListItem({
  allowArchive = true,
  item,
  showDocumentActions = true,
  showTypeLabel = true,
  showCheckboxes = false,
  setCheckedItems,
  checkedItems = [],
  baseCategory,
  flagType,
  radius = 'null',
  displayDate = true,
  onClick,
  isPackageEditable = true,
  showFlagToEPC: showFlagToEPCProp,
  showFlagIndicator = true,
}: DocumentListItemProps) {
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const { flagDrivenPackageReview } = useFlags();
  const { type, files, meta } = item || {};
  const { isOpen, handleToggle, handleClose } = useOpenClose();
  const [archiveDocument, { isLoading: isArchiving }] = useArchiveDocumentMutation();
  const { getFlag, getFlagsByFlagType, shouldHide, getVisibleFlags, flags } = useContext(QueueFlagsContext);
  const documentTitle = documentTitles[type] || type;
  const viewUrls = files[0]?.viewUrls;
  const thumbnailUrl = getThumbnailUrl(viewUrls);
  const hasThumbnail = !!getStrictThumbnailUrl(viewUrls);
  const originalUrl = getOriginalUrl(viewUrls);
  const fileName = files[0]?.originalName;
  const isImage = files[0]?.contentType?.includes('image');
  const itemId = item?.id?.toString() || '';
  const accountId = item?.accountId?.toString() || '';
  const isChecked = checkedItems.includes(itemId);
  const { handleImageError } = useDocumentUrl();

  const allFlagsForItem = useMemo(() => {
    const flagsForFiles = files.flatMap((file: DocumentFile, index: number) => {
      const dataKeyWithSuffix = `${itemId}-${index}`;
      const currentFlag = getFlag([dataKeyWithSuffix, itemId], file.originalName, false);
      const currentFlags = flagType
        ? getFlagsByFlagType(flagType, false, [dataKeyWithSuffix, itemId], file.originalName)
        : [];
      return flagDrivenPackageReview
        ? [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : [])]
        : [currentFlag];
    });
    const filteredFlags = flagsForFiles.filter((flag) => Boolean(flag)) as FlagType[];

    return getVisibleFlags(filteredFlags);
  }, [files, itemId, getFlag, flagType, getFlagsByFlagType, flagDrivenPackageReview, getVisibleFlags]);

  const attachedDocumentFlags = useMemo(
    () => flags.filter((f) => !f.resolved && (f.attachedDocumentIds?.includes(itemId) ?? false)),
    [flags, itemId],
  );
  const isAttachedToFlag = attachedDocumentFlags.length > 0;
  const hasFlags = showFlagIndicator && Boolean(allFlagsForItem.length || isAttachedToFlag);
  const flagHasId = allFlagsForItem.some((flag) => flag?.id);
  const { isPhone } = useBreakpoint();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const milestonePackageURI = location.pathname.split('/')[3];

  const queueType = MilestoneRouteToQueueTypeMap[milestonePackageURI as MilestonePackageRoute];
  const { data: queue } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: id,
      type: queueType,
    },
    { skip: !id || !queueType || showFlagToEPCProp !== undefined || !flagDrivenPackageReview },
  );

  const showFlagToEPC =
    showFlagToEPCProp !== undefined
      ? showFlagToEPCProp
      : flagDrivenPackageReview
        ? !(queue?.status && PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES.has(queue?.status) && !isFinCoUser)
        : true;

  const handleArchive = async () => {
    if (hasFlags && showFlagToEPC && !flagHasId) {
      toast.error('Please upload a new document to fix the flagged issue(s). You cannot archive this document.');
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to Archive this Document? This action cannot be undone.',
    );

    if (!confirmed) return;

    try {
      await archiveDocument({
        id: item?.id?.toString() || '',
        accountId: item?.accountId?.toString() || '',
        invalidateAllTags: false,
      }).unwrap();
      toast.success('Document archived successfully');
      handleClose();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error archiving uploaded document');
    } finally {
      handleClose();
    }
  };
  const documentCreatedAtDate = DateTime.fromISO(meta?.createdAt || '');
  const documentCreatedAtFormat =
    documentCreatedAtDate.year === DateTime.now().year ? 'LLL d, h:mm a' : 'LLL d, yyyy h:mm a';

  const renderDrivenPackageReviewVersion = () => (
    <Box
      borderWidth={`xs 0 0 0`}
      borderColor="separator"
      background={hasFlags && showFlagToEPC ? 'danger' : 'primary'}
      radius={radius}
      className={classNames({
        [styles.flaggedItem]: hasFlags && showFlagToEPC,
      })}
      style={{ position: 'relative' }}
    >
      {hasFlags && showFlagToEPC && (
        <FlagContent
          files={files}
          itemId={itemId}
          flagType={flagType}
          allFlagsForItem={allFlagsForItem}
          flagDrivenPackageReview={true}
          hasFlags={hasFlags}
          attachedDocumentFlags={attachedDocumentFlags}
        />
      )}
      <Box direction="row" width="100" alignItems="center" className={isChecked ? 'checked' : ''}>
        {showCheckboxes && !hasFlags && (
          <CheckboxInput
            id={itemId}
            className={`checkbox ${isChecked ? 'checked' : ''}`}
            key={itemId}
            label=""
            padding="md 0 md lg"
            isChecked={checkedItems.includes(itemId)}
            onChange={() => {
              if (typeof setCheckedItems === 'function') {
                setCheckedItems((prev: string[]) => {
                  if (prev.includes(itemId)) {
                    return prev.filter((id) => id !== itemId);
                  }
                  return [...prev, itemId];
                });
              }
            }}
          />
        )}
        <ImageRenderer
          isImage={isImage}
          hasThumbnail={hasThumbnail}
          thumbnailUrl={thumbnailUrl ?? originalUrl}
          fileName={fileName}
          hasFlags={hasFlags && showFlagToEPC}
          onClick={onClick}
          flagDrivenPackageReview={true}
          onImageError={(e) => handleImageError(e, accountId, itemId, ViewUrlType.thumbnail)}
        />
        <FileInfo
          files={files}
          itemId={itemId}
          documentTitle={documentTitle}
          showTypeLabel={showTypeLabel}
          getFlag={getFlag}
          getFlagsByFlagType={getFlagsByFlagType}
          flagType={flagType}
          flagDrivenPackageReview={flagDrivenPackageReview}
          isPhone={isPhone}
          onClick={onClick}
          hasFlags={hasFlags && showFlagToEPC}
          isImage={isImage}
          internalReferences={item?.internalReferences as unknown as { fieldname: string; value: string }[]}
        />
        <DateDisplay displayDate={displayDate} meta={meta} documentCreatedAtFormat={documentCreatedAtFormat} />
        <ActionButtons originalUrl={originalUrl} fileName={fileName} onClick={onClick} />
        <DocumentActions
          showDocumentActions={showDocumentActions && isPackageEditable && showFlagIndicator}
          allowArchive={allowArchive && !hasFlags}
          isOpen={isOpen}
          isArchiving={isArchiving}
          handleArchive={handleArchive}
          handleClose={handleClose}
          handleToggle={handleToggle}
          shouldHide={shouldHide}
          item={item}
          itemId={itemId}
          fileName={fileName}
          type={type}
          baseCategory={baseCategory}
          flagType={flagType}
          flagDrivenPackageReview={true}
          hasFlags={hasFlags}
        />
      </Box>
    </Box>
  );

  const renderStandardVersion = () => (
    <Box borderWidth="xs 0 0 0" borderColor="separator" background={hasFlags ? 'danger' : 'primary'} radius={radius}>
      <Box padding={{ base: 'md 0', tablet: '0' }} direction="row" width="100" alignItems="center">
        <Box width="100" direction="row" alignItems="center">
          {hasFlags && showFlagToEPC && (
            <FlagContent
              files={files}
              itemId={itemId}
              flagType={flagType}
              getFlag={getFlag}
              flagDrivenPackageReview={false}
              hasFlags={hasFlags}
              attachedDocumentFlags={attachedDocumentFlags}
            />
          )}
          <ImageRenderer
            isImage={isImage}
            hasThumbnail={hasThumbnail}
            thumbnailUrl={thumbnailUrl ?? originalUrl}
            fileName={fileName}
            hasFlags={hasFlags && showFlagToEPC}
            onClick={onClick}
            flagDrivenPackageReview={false}
            onImageError={(e) => handleImageError(e, accountId, itemId, ViewUrlType.thumbnail)}
          />
          <FileInfo
            files={files}
            itemId={itemId}
            documentTitle={documentTitle}
            showTypeLabel={showTypeLabel}
            getFlag={getFlag}
            getFlagsByFlagType={getFlagsByFlagType}
            flagType={flagType}
            flagDrivenPackageReview={flagDrivenPackageReview}
            isPhone={isPhone}
            onClick={onClick}
            hasFlags={hasFlags && showFlagToEPC}
            isImage={isImage}
            internalReferences={item?.internalReferences as unknown as { fieldname: string; value: string }[]}
          />
          <DateDisplay displayDate={displayDate} meta={meta} documentCreatedAtFormat={documentCreatedAtFormat} />
          <ActionButtons originalUrl={originalUrl} fileName={fileName} onClick={onClick} />
          <DocumentActions
            showDocumentActions={showDocumentActions && isPackageEditable}
            allowArchive={allowArchive}
            isOpen={isOpen}
            isArchiving={isArchiving}
            handleArchive={handleArchive}
            handleClose={handleClose}
            handleToggle={handleToggle}
            shouldHide={shouldHide}
            item={item}
            itemId={itemId}
            fileName={fileName}
            type={type}
            baseCategory={baseCategory}
            flagType={flagType}
            flagDrivenPackageReview={false}
            hasFlags={hasFlags}
          />
        </Box>
      </Box>
    </Box>
  );
  return flagDrivenPackageReview ? renderDrivenPackageReviewVersion() : renderStandardVersion();
}
